import { HelpText } from '#components/atoms/forms/index.ts';
import { UnreachableCodeError } from '#util/assert-unreachable.ts';
import type { CONFIG_OVERRIDE_LABELS } from '../labels/ConfigOverrideLabels.ts';

type Props = {
  configOverride: keyof typeof CONFIG_OVERRIDE_LABELS | '';
  hostType?: 'lighthouse' | 'host' | 'relay';
};

/**
 * {@link ConfigOverrideHelpText} maps from a config override key to accompaning help text
 *  for use in the tag's ConfigOverridesForm and the host's HostAdvancedFormPart
 */
export function ConfigOverrideHelpText({ configOverride, hostType }: Props) {
  switch (configOverride) {
    case 'definednet.dns.resolver_addrs':
      return (
        <HelpText>
          <code>definednet.dns.resolver_addrs</code> are the DNS resolvers the host should use while the tunnel is up.
          Accepts a comma-separated list of bare IPv4 and IPv6 addresses, with no port. Only mobile devices apply this
          setting today.
        </HelpText>
      );
    case 'definednet.dns.match_domains':
      return (
        <HelpText>
          <code>definednet.dns.match_domains</code> limits the resolvers above to these domains, leaving everything else
          to the deviceʼs normal DNS. Accepts a comma-separated list of domains. Only iOS applies this setting today —
          on Android the resolvers serve all domains.
        </HelpText>
      );
    case 'definednet.dns.search_domains':
      return (
        <HelpText>
          <code>definednet.dns.search_domains</code> are the domains appended to unqualified names before they are
          looked up. Accepts a comma-separated list of domains. Only mobile devices apply this setting today.
        </HelpText>
      );
    case 'lighthouse.dns.port':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/lighthouse/#lighthousedns" className="a" target="_blank">
            <code>lighthouse.dns.port</code>
          </a>{' '}
          controls the port the dns listener should bind to. The default is 53, if not set here.
        </HelpText>
      );
    case 'listen.batch':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/listen/#batch" className="a" target="_blank">
            <code>listen.batch</code>
          </a>{' '}
          is the max number of packets to pull from the kernel for each syscall (under systems that support recvmmsg).
        </HelpText>
      );
    case 'listen.read_buffer':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/listen#read_buffer-write_buffer"
            className="a"
            target="_blank"
          >
            <code>listen.read_buffer</code>
          </a>{' '}
          configures socket read buffer for the udp side (outside)
        </HelpText>
      );
    case 'listen.write_buffer':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/listen#read_buffer-write_buffer"
            className="a"
            target="_blank"
          >
            <code>listen.write_buffer</code>
          </a>{' '}
          configures socket write buffer for the udp side (outside)
        </HelpText>
      );
    case 'routines':
      return (
        <HelpText>
          Linux only:{' '}
          <a href="https://nebula.defined.net/docs/config/routines/" className="a" target="_blank">
            <code>routines</code>
          </a>{' '}
          is the number of thread pairs to run that consume from the tun and UDP queues. See docs for important caveats.
        </HelpText>
      );
    case 'listen.udp_offloads':
      return (
        <HelpText>
          Linux only:{' '}
          <a href="https://nebula.defined.net/docs/config/listen/#listenudp_offloads" className="a" target="_blank">
            <code>listen.udp_offloads</code>
          </a>{' '}
          enables UDP segmentation and receive offloads (GSO/GRO) on the listen socket, batching packets to reduce
          syscall overhead. The default is false.
        </HelpText>
      );
    case 'tun.use_offloads':
      return (
        <HelpText>
          Linux only:{' '}
          <a href="https://nebula.defined.net/docs/config/tun/#tunuse_offloads" className="a" target="_blank">
            <code>tun.use_offloads</code>
          </a>{' '}
          enables checksum and segmentation offloads on the nebula interface, batching packets to reduce syscall
          overhead. The default is false.
        </HelpText>
      );
    case 'tun.disabled':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/tun/#tundisabled" className="a" target="_blank">
            <code>tun.disabled</code>
          </a>{' '}
          Allows the nebula interface (tun) to be disabled. This can be useful for lighthouses or relays in docker
          instances without sudo.
        </HelpText>
      );
    case 'tun.mtu':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/tun/#tunmtu" className="a" target="_blank">
            <code>tun.mtu</code>
          </a>{' '}
          sets the MTU for every packet. The default is 1300, safe for internet routed packets.
        </HelpText>
      );
    case 'tun.tx_queue':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/tun/#tuntx_queue" className="a" target="_blank">
            <code>tun.tx_queue</code>
          </a>{' '}
          sets the transmit queue length. If you notice lots of transmit drops on the tun it may help to increase this
          number.
        </HelpText>
      );
    case 'lighthouse.dns.host':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/lighthouse/#lighthousedns" className="a" target="_blank">
            <code>lighthouse.dns.host</code>
          </a>{' '}
          controls the IP the dns listener should bind to. If binding to the overlay IP address, you must create a
          firewall rule allowing access to the specified port.
        </HelpText>
      );
    case 'listen.host':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/listen/#listenhost" className="a" target="_blank">
            <code>listen.host</code>
          </a>{' '}
          is the IP of the interface to use when binding the listener.{' '}
          {hostType === 'lighthouse' ?
            'The lighthouses default is 0.0.0.0 for all IPv4 interfaces. To enable IPv6, use “[::]” instead (not recommended). A hostname can also be used.'
          : 'The default is [::] for all IPv4 and IPv6 interfaces. A hostname can also be used.'}
        </HelpText>
      );
    case 'logging.timestamp_format':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/logging/#loggingtimestamp_format"
            className="a"
            target="_blank"
          >
            <code>logging.timestamp_format</code>
          </a>{' '}
          allows configuring the timestamp format, specified in{' '}
          <a href="https://golang.org/pkg/time/#pkg-constants" className="a" target="_blank" rel="noreferrer">
            Go time format
          </a>
          .
        </HelpText>
      );
    case 'stats.host':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statshost" className="a" target="_blank">
            <code>stats.host</code>
          </a>{' '}
          specifies the consuming server to push Graphite stats to.
        </HelpText>
      );
    case 'stats.prefix':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statsprefix" className="a" target="_blank">
            <code>stats.prefix</code>
          </a>{' '}
          specifies the prefix for Graphite metrics, default is <code>“nebula”</code>.
        </HelpText>
      );
    case 'stats.namespace':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statsnamespace" className="a" target="_blank">
            <code>stats.namespace</code>
          </a>{' '}
          is a prefix for Prometheus metrics, recommended to be set as <code>nebula</code>
        </HelpText>
      );
    case 'stats.subsystem':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statssubsystem" className="a" target="_blank">
            <code>stats.subsystem</code>
          </a>{' '}
          specifies the Prometheus subsystem.
        </HelpText>
      );
    case 'stats.listen':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statslisten" className="a" target="_blank">
            <code>stats.listen</code>
          </a>{' '}
          specifies what interface the Prometheus stats should listen on.
        </HelpText>
      );
    case 'stats.path':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statspath" className="a" target="_blank">
            <code>stats.path</code>
          </a>{' '}
          is the path to serve the prometheus stats under, recommended to set be set to <code>“/metrics”</code>. Must
          start with <code>“/”</code>.
        </HelpText>
      );
    case 'tun.dev':
      return (
        <HelpText>
          Linux only:{' '}
          <a href="https://nebula.defined.net/docs/config/tun/#tundev" className="a" target="_blank">
            <code>tun.dev</code>
          </a>{' '}
          sets the interface name for your nebula interface.
        </HelpText>
      );
    case 'stats.interval':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statsinterval" className="a" target="_blank">
            <code>stats.interval</code>
          </a>{' '}
          is the stats resolution in seconds.
        </HelpText>
      );
    case 'logging.format':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/logging/#loggingformat" className="a" target="_blank">
            <code>logging.format</code>
          </a>{' '}
          allows choosing text or JSON output
        </HelpText>
      );
    case 'logging.level':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/logging/#logginglevel" className="a" target="_blank">
            <code>logging.level</code>
          </a>{' '}
          allows configuring the logging level.
        </HelpText>
      );
    case 'stats.type':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statstype" className="a" target="_blank">
            <code>stats.type</code>
          </a>{' '}
          allows choosing your stats provider. Options are graphite and prometheus. If unset, nebula will not report
          statistics to a provider. This will determine other available options. A difference between the two is that
          Prometheus is a polling based system, whereas Graphite is push based.
        </HelpText>
      );
    case 'stats.lighthouse_metrics':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statslighthouse_metrics" className="a" target="_blank">
            <code>stats.lighthouse_metrics</code>
          </a>{' '}
          enables counter metrics for meta packets.
        </HelpText>
      );
    case 'stats.message_metrics':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/stats/#statsmessage_metrics" className="a" target="_blank">
            <code>stats.message_metrics</code>
          </a>{' '}
          enables detailed counter metrics for lighthouse packets.
        </HelpText>
      );
    case 'lighthouse.serve_dns':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/lighthouse/#lighthouseserve_dns"
            className="a"
            target="_blank"
          >
            <code>lighthouse.serve_dns</code>
          </a>{' '}
          starts a DNS listener that responds to A and TXT queries.
        </HelpText>
      );
    case 'lighthouse.remote_allow_list':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/lighthouse/#lighthouseremote_allow_list"
            className="a"
            target="_blank"
          >
            <code>lighthouse.remote_allow_list</code>
          </a>{' '}
          allows you to control IP ranges that this node will consider when handshaking to another node.
        </HelpText>
      );
    case 'lighthouse.local_allow_list':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/lighthouse/#lighthouselocal_allow_list"
            className="a"
            target="_blank"
          >
            <code>lighthouse.local_allow_list</code>
          </a>{' '}
          allows you to filter which local IP addresses we advertise to the lighthouses.
        </HelpText>
      );
    case 'lighthouse.advertise_addrs':
      return (
        <HelpText>
          <a
            href="https://nebula.defined.net/docs/config/lighthouse/#lighthouseadvertise_addrs"
            className="a"
            target="_blank"
          >
            <code>lighthouse.advertise_addrs</code>
          </a>{' '}
          are routable addresses that will be included along with discovered addresses to report to the lighthouse.
          Accepts a comma-separated list of IPv4 and IPv6 ip:port values.
        </HelpText>
      );
    case 'lighthouse.interval':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/lighthouse/#lighthouseinterval" className="a" target="_blank">
            <code>lighthouse.interval</code>
          </a>{' '}
          is the number of seconds between updates from this node to a lighthouse. The default is 60.
        </HelpText>
      );
    case 'preferred_ranges':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/preferred-ranges/" className="a" target="_blank">
            <code>preferred_ranges</code>
          </a>{' '}
          sets the priority order for underlay IP addresses. Accepts a comma-separated list of IPv4 and IPv6 CIDR
          ranges.
        </HelpText>
      );
    case 'static_map.network':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/static-map/#static_mapnetwork" className="a" target="_blank">
            <code>static_map.network</code>
          </a>{' '}
          controls which IP version to use for static host map lookups. Options are ip (both), ip4, or ip6.
        </HelpText>
      );
    case 'punchy.delay':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/punchy/#punchydelay" className="a" target="_blank">
            <code>punchy.delay</code>
          </a>{' '}
          is the period of time Nebula waits between receiving a Lighthouse handshake notification and sending an empty
          packet in order to try to punch a hole in the NAT firewall. The default is 1s.
        </HelpText>
      );
    case 'punchy.respond_delay':
      return (
        <HelpText>
          <a href="https://nebula.defined.net/docs/config/punchy/#punchyrespond_delay" className="a" target="_blank">
            <code>punchy.respond_delay</code>
          </a>{' '}
          is the period of time Nebula waits between receiving a Lighthouse handshake notification and attempting its
          own “reverse” handshake with the initiating peer. The default is 5s.
        </HelpText>
      );
    case '':
    case 'prometheus':
    case 'graphite':
      // no helptext
      return null;
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new UnreachableCodeError(configOverride, `Unexpected config override key found, key: "${configOverride}"`);
  }
}
